import { useCallback } from 'react';
import useLog from 'hooks/useLog';
import { dayjs, ET } from 'util/shared/date';

interface TimeSeriesValue {
  open: number;
  close: number | string;
}

interface TimeSeriesData {
  values: TimeSeriesValue[];
}

interface ApiResponse {
  [symbol: string]: TimeSeriesData;
}
// Response output is correctly sorted, just take a few points to verify correctness and ordering
const OUTPUT_SIZE = 5;

/**
 * Get epoch seconds for 3:45 PM of the given date
 * @param {string} date - Date in YYYY-MM-DD format
 * @returns {number} - Epoch seconds for 3:45 PM of the given date
 */
const getEpochForEnddOfDay = (date: string): number => {
  const parsedDate = dayjs(date, 'YYYY-MM-DD');
  const endOfDay = parsedDate
    .tz(ET)
    .set('hour', 15)
    .set('minute', 45)
    .set('second', 0)
    .millisecond(0);
  return Math.floor(endOfDay.valueOf() / 1_000);
};

const useClosingPrices = () => {
  const { fetchAPIWithLog } = useLog('useLatestPrices');

  const getTimeSeries = useCallback(
    async (instruments: string[], date?: string): Promise<ApiResponse> => {
      const symbol = instruments.map(encodeURIComponent).join(',');

      if (date == null || !dayjs(date).isValid()) {
        return (await fetchAPIWithLog(
          `v1/twelve_series?symbol=${symbol}&interval=1min&outputsize=${OUTPUT_SIZE}&order=desc`,
        )) as ApiResponse;
      }

      const endEpoch = getEpochForEnddOfDay(date);
      return (await fetchAPIWithLog(
        `v1/twelve_series?symbol=${symbol}&interval=1min&outputsize=${OUTPUT_SIZE}&order=desc&start_date=${date}&end_epoch=${endEpoch}`,
      )) as ApiResponse;
    },
    [fetchAPIWithLog],
  );

  const getClosingPrices = useCallback(
    async (syms: string[], date?: string): Promise<Record<string, number>> => {
      const data = await getTimeSeries(syms, date);
      // v1/twelve_data returns numeric values ScyllaDB results like SPX and strings for twelve_data results like AAPL
      const entries = Object.entries(data)
        .map(([sym, val]) => {
          return [sym, parseFloat(val.values?.[0]?.close as string)];
        })
        .filter(([_, v]) => !isNaN(v as number));
      return Object.fromEntries(entries) as Record<string, number>;
    },
    [getTimeSeries],
  );

  const getClosingPrice = useCallback(
    async (sym: string, date?: string): Promise<number | undefined> => {
      const prices = await getClosingPrices([sym], date);
      return prices[sym];
    },
    [getClosingPrices],
  );

  return { getClosingPrices, getClosingPrice };
};

export default useClosingPrices;
